import { type ToolCallMessagePartProps, useAuiState } from '@assistant-ui/react';
import { IconExclamationCircle, IconMessageUser } from '@tabler/icons-react';

import {
  type AskUserQuestionOutput,
  AskUserQuestionOutputSchema,
  type AskUserQuestionParameters,
  type StepperPayload,
} from './schema.ts';
import { StepperCard } from '#plugin-chat/components/tool-display/StepperCard.tsx';
import { ToolDisplay } from '#plugin-chat/components/tool-display/ToolDisplay.tsx';

export function RenderAskUserQuestionTool({
  args,
  result,
  resume,
  interrupt,
}: ToolCallMessagePartProps<AskUserQuestionParameters, AskUserQuestionOutput>) {
  const threadIsRunning = useAuiState((s) => s.thread.isRunning);

  // WORKAROUND: Don't wait for interrupt state due to upstream race condition.
  // First human() call doesn't trigger status.type === 'requires-action'.
  // Show UI immediately when we have valid slides and no result yet.
  const shouldShowUi = !result && args.slides && args.slides.length > 0;

  // Parse the current slide index from interrupt payload (if available)
  const getCurrentSlideIndex = (): number => {
    // The interrupt payload contains slideIndex when sent from execute
    return (interrupt?.payload as StepperPayload | undefined)?.slideIndex ?? 0;
  };

  const currentSlideIndex = getCurrentSlideIndex();

  return (
    <ToolDisplay>
      <ToolDisplay.Running>{null}</ToolDisplay.Running>
      <ToolDisplay.AwaitingInput>
        {(threadIsRunning || (interrupt?.payload as React.ReactNode)) && shouldShowUi && (
          <StepperCard
            currentSlide={currentSlideIndex}
            isStreaming={threadIsRunning}
            onSelect={(slideIndex: number, selectedIds: string[]) => {
              resume({ selectedIds, slideIndex });
            }}
            slides={args.slides ?? []}
          />
        )}
      </ToolDisplay.AwaitingInput>
      <ToolDisplay.Complete
        color={result?.error ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-green-6)'}
        icon={result?.error ? IconExclamationCircle : IconMessageUser}
      >
        <CompletionMessage result={result} />
      </ToolDisplay.Complete>
    </ToolDisplay>
  );
}

interface CompletionMessageProps {
  result: unknown;
}

function CompletionMessage({ result }: CompletionMessageProps) {
  if (!result) {
    return <div>Completed</div>;
  }

  const resultObj = result as { error?: string };
  if (resultObj.error) {
    return <div>{resultObj.error}</div>;
  }

  // Parse result through the output schema to ensure type safety
  // TODO: auto parse, or check if aui does it already
  const parsed = AskUserQuestionOutputSchema.safeParse(result);
  if (parsed.success && parsed.data.data) {
    const dataEntries = Object.entries(parsed.data.data);
    if (dataEntries.length === 0) {
      return <span>No choices were made.</span>;
    }

    return (
      <div>
        {dataEntries.map(([title, choices]) => (
          <span key={title}>
            {title} &rarr; <strong>{choices.join(', ')}</strong>
          </span>
        ))}
      </div>
    );
  }

  return <div>Choice received</div>;
}
